import i18n, { Resource } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Nederlands is de brontaal en tevens fallback. Nieuwe talen voeg je toe door
// een map src/locales/<taal>/ aan te maken en die hieronder te registreren in
// LANGUAGE_CONTEXTS. Per taal worden ALLE JSON-bestanden in de map automatisch
// ingeladen en samengevoegd, zodat elke component zijn eigen namespace-bestand
// kan hebben (bijv. locales/nl/recorder.json) zonder dat dit bestand aangepast
// hoeft te worden.
export const SUPPORTED_LANGUAGES = ['nl', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

// Weergavenaam per taal, altijd in de eigen taal (endonym). Gebruikt door de
// taalswitcher. Korte code (NL/EN) wordt afgeleid van de sleutel zelf.
export const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
    nl: 'Nederlands',
    en: 'English',
};

type JsonObject = { [key: string]: unknown };

function isPlainObject(value: unknown): value is JsonObject {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Diepe merge zodat meerdere bestanden binnen dezelfde taal kunnen bijdragen aan
// dezelfde top-level key (bijv. zowel common.json als een component die iets aan
// common toevoegt) zonder elkaar te overschrijven.
function deepMerge(target: JsonObject, source: JsonObject): JsonObject {
    for (const key of Object.keys(source)) {
        const sourceValue = source[key];
        const targetValue = target[key];
        if (isPlainObject(sourceValue) && isPlainObject(targetValue)) {
            target[key] = deepMerge({ ...targetValue }, sourceValue);
        } else {
            target[key] = sourceValue;
        }
    }
    return target;
}

// require.context is een webpack-feature (react-scripts gebruikt webpack) waarmee
// we alle JSON-bestanden in een map kunnen inladen zonder ze los te importeren.
declare const require: {
    context(
        directory: string,
        useSubdirectories: boolean,
        regExp: RegExp,
    ): {
        keys(): string[];
        (id: string): JsonObject | { default: JsonObject };
    };
};

function loadLanguage(context: ReturnType<typeof require.context>): JsonObject {
    return context.keys().reduce<JsonObject>((acc, key) => {
        const mod = context(key);
        const data = (mod as { default?: JsonObject }).default ?? (mod as JsonObject);
        return deepMerge(acc, data);
    }, {});
}

const LANGUAGE_CONTEXTS: Record<SupportedLanguage, ReturnType<typeof require.context>> = {
    nl: require.context('../locales/nl', false, /\.json$/),
    en: require.context('../locales/en', false, /\.json$/),
};

export const resources: Resource = Object.fromEntries(
    SUPPORTED_LANGUAGES.map((lang) => [lang, { translation: loadLanguage(LANGUAGE_CONTEXTS[lang]) }]),
);

void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        fallbackLng: 'nl',
        supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
        nonExplicitSupportedLngs: true,
        interpolation: {
            escapeValue: false, // React escapet zelf al
        },
        detection: {
            // Volgorde waarin de taalkeuze wordt bepaald; keuze wordt onthouden in localStorage.
            order: ['localStorage', 'navigator', 'htmlTag'],
            lookupLocalStorage: 'language',
            caches: ['localStorage'],
        },
    });

export default i18n;
